import React, { useCallback } from 'react'
import { useHistory, useLocation } from 'react-router-dom';
import { HiOutlineSearch } from 'react-icons/hi';
import EmptyState from '../components/EmptyState';
import InfiniteProductGrid, { stylesLabel } from '../components/InfiniteProductGrid';
import useItemFeed from '../util/useItemFeed';
import '../css/allitems.css'
import brand, { brandText } from '../brand'

import { GENDERS, AGES } from '../util/shopFilters'

// Filters live in the URL (?for=girls&age=2) so a filtered view can be shared. Products keep loading as the
// shopper scrolls (useItemFeed); an old ?page= in a link is simply ignored.
const readFilters = (search) => {
    const q = new URLSearchParams(search)
    const g = GENDERS.find(x => x.key === q.get('for')) || GENDERS[0]
    const a = AGES.find(x => x.value === q.get('age')) || AGES[0]
    return { gender: g.value, ageRange: a.value }
}

const AllItemsView = () => {

    const history = useHistory();
    const location = useLocation();
    const { gender, ageRange } = readFilters(location.search)
    const feed = useItemFeed({ age: ageRange, gender })

    const setFilters = (next) => {
        const merged = { gender, ageRange, ...next }
        const q = new URLSearchParams()
        const g = GENDERS.find(x => x.value === merged.gender)
        if (g && g.key !== 'all') q.set('for', g.key)
        if (merged.ageRange !== '0') q.set('age', merged.ageRange)
        history.replace({ pathname: '/items/all', search: q.toString() ? '?' + q.toString() : '' })
    }

    const openView = useCallback((id) => {
        history.push({ pathname: '/shop/items/view/' + id, state: { id } })
    }, [history])

    const filtered = gender !== '3' || ageRange !== '0'
    const genderLabel = GENDERS.find(x => x.value === gender).label
    const ageLabel = AGES.find(x => x.value === ageRange).label
    const title = gender === '3' ? 'All products' : `For ${genderLabel.toLowerCase()}`

    return (
        <main className='mv-page'>
            <div className='mv-page-head'>
                <div>
                    <h1 className='mv-list-title'>{title}</h1>
                    <p className='mv-page-sub'>
                        {ageRange !== '0' ? `Ages ${ageLabel}` : brandText(brand.allItemsSubtitle)}
                        {feed.total > 0 && feed.status !== 'loading' && ` · ${stylesLabel(feed.total)}`}
                    </p>
                </div>
            </div>

            <section className='mv-filters' aria-label='Filters'>
                <div className='mv-filter-group'>
                    <span className='mv-filter-label' id='filter-for'>Shopping for</span>
                    <div className='mv-chip-row' role='radiogroup' aria-labelledby='filter-for'>
                        {GENDERS.map(g =>
                            <button type='button' key={g.value} role='radio' aria-checked={gender === g.value}
                                className={gender === g.value ? 'mv-filter-chip active' : 'mv-filter-chip'}
                                onClick={() => setFilters({ gender: g.value })}>
                                {g.label}
                            </button>
                        )}
                    </div>
                </div>
                <div className='mv-filter-group'>
                    <span className='mv-filter-label' id='filter-age'>Age</span>
                    <div className='mv-chip-row' role='radiogroup' aria-labelledby='filter-age'>
                        {AGES.map(a =>
                            <button type='button' key={a.value} role='radio' aria-checked={ageRange === a.value}
                                className={ageRange === a.value ? 'mv-filter-chip active' : 'mv-filter-chip'}
                                onClick={() => setFilters({ ageRange: a.value })}>
                                {a.label}
                            </button>
                        )}
                    </div>
                </div>
                {filtered &&
                    <button type='button' className='mv-link-btn mv-filter-clear' onClick={() => setFilters({ gender: '3', ageRange: '0' })}>
                        Clear filters
                    </button>}
            </section>

            <InfiniteProductGrid feed={feed} onOpen={openView}
                empty={<EmptyState icon={<HiOutlineSearch />} title='No styles match these filters'
                    text='Try a different age or show products for everyone.'
                    actionLabel={filtered ? 'Clear filters' : undefined}
                    onAction={filtered ? () => setFilters({ gender: '3', ageRange: '0' }) : undefined} />} />
        </main>
    )
}

export default AllItemsView
